import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './ProfileSettings.css';
import { supabase, signOut } from '../utils/supabaseClient';
import { uploadFile, getFileUrl } from '../utils/storageService';
import { normalizeTimezone } from '../utils/timezone';
import { languages, nativeLanguages, countries, timezones } from '../data/profileOptions';
import { useDeviceCheck } from '../hooks/useDeviceCheck';
import { usePasswordChange } from '../hooks/usePasswordChange';
import { useEmailChange } from '../hooks/useEmailChange';
import DeviceSettingsModal from './ProfileSettings/DeviceSettingsModal';
import SearchableDropdown from './ProfileSettings/SearchableDropdown';
import PaymentsPlanTab from './ProfileSettings/PaymentsPlanTab';
import NotificationsTab from './ProfileSettings/NotificationsTab';
import SecurityTab from './ProfileSettings/SecurityTab';
import { NavIcon } from './ProfileSettingsIcons';
import ConfirmSignOutModal from './ConfirmSignOutModal';

// Interface language is saved as profile metadata but doesn't actually
// translate the UI yet (no i18n system in the project) — restricted to
// English only so the picker doesn't imply a working language switch.
const INTERFACE_LANGUAGES = languages.filter(l => l.value === 'english');

const ProfileSettings = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('personal-info');
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);

  // Sign-out from the profile screen — the sidebar is hidden on mobile,
  // so without a button here mobile users had no way to log out.
  const handleSignOut = async () => {
    setShowSignOutConfirm(false);
    try { await signOut(); } catch (e) { console.warn('signOut failed:', e); }
    try {
      localStorage.removeItem('currentUser');
      localStorage.removeItem('isAuthenticated');
    } catch (_) {}
    navigate('/login');
  };

  // Get user data from localStorage (fallback)
  const getCurrentUserFromStorage = () => {
    const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
    return {
      name: user.name || 'User',
      email: user.email || 'user@example.com',
      phone: user.phone || ''
    };
  };
  
  const [profileData, setProfileData] = useState({
    ...getCurrentUserFromStorage(),
    phone: getCurrentUserFromStorage().phone || ''
  });
  const [originalData, setOriginalData] = useState({
    ...getCurrentUserFromStorage(),
    phone: getCurrentUserFromStorage().phone || ''
  });
  const [hasNameChanges, setHasNameChanges] = useState(false);
  const [hasPhoneChanges, setHasPhoneChanges] = useState(false);
  const [languageSearch, setLanguageSearch] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState({ flag: '🇬🇧', name: 'English', value: 'english' });
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [nativeLanguageSearch, setNativeLanguageSearch] = useState('');
  const [selectedNativeLanguage, setSelectedNativeLanguage] = useState({ flag: '🇬🇧', name: 'English', value: 'english' });
  const [showNativeLanguageDropdown, setShowNativeLanguageDropdown] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [selectedCountry, setSelectedCountry] = useState({ flag: '🇦🇲', name: 'Armenia', value: 'armenia' });
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [timezoneSearch, setTimezoneSearch] = useState('');
  const [selectedTimezone, setSelectedTimezone] = useState({ name: 'Yerevan (UTC+4)', value: 'Asia/Yerevan' });
  const [showTimezoneDropdown, setShowTimezoneDropdown] = useState(false);
  const fileInputRef = React.useRef(null);
  const [savedToast, setSavedToast] = useState(false);
  const savedToastTimeoutRef = React.useRef(null);

  // Load user data from Supabase on component mount
  useEffect(() => {
    const loadUserData = async () => {
      try {
        // Получаем пользователя напрямую из Supabase Auth, чтобы избежать проблем с localStorage
        const { data: { user: authUser } } = await supabase.auth.getUser();
        
        if (authUser) {
          // Получаем данные преподавателя из таблицы teachers
          const { data: teacherData, error: teacherError } = await supabase
            .from('teachers')
            .select('*')
            .eq('auth_user_id', authUser.id)
            .single();
          
          if (!teacherError && teacherData) {
            const profile = {
              name: teacherData.name || 'Teacher',
              email: teacherData.email || 'teacher@example.com',
              phone: teacherData.phone || ''
            };
            setProfileData(profile);
            setOriginalData(profile);

            if (teacherData.timezone) {
              const foundTz = timezones.find(t => t.value === teacherData.timezone) || timezones.find(t => normalizeTimezone(t.value) === normalizeTimezone(teacherData.timezone));
              if (foundTz) setSelectedTimezone(foundTz);
            }
            if (teacherData.interface_language) {
              const foundLang = INTERFACE_LANGUAGES.find(l => l.value === teacherData.interface_language);
              if (foundLang) setSelectedLanguage(foundLang);
            }
            if (teacherData.native_language) {
              const foundNativeLang = nativeLanguages.find(l => l.value === teacherData.native_language);
              if (foundNativeLang) setSelectedNativeLanguage(foundNativeLang);
            }
            if (teacherData.country) {
              const foundCountry = countries.find(c => c.value === teacherData.country);
              if (foundCountry) setSelectedCountry(foundCountry);
            }

            // Загружаем фото, если есть
            if (teacherData.photo) {
              // Если photo - это полный URL (включая signed URL), используем его
              // Если photo - это путь к файлу, получаем signed URL
              if (teacherData.photo.startsWith('http')) {
                setProfilePhoto(teacherData.photo);
              } else {
                // Это путь к файлу, нужно получить signed URL
                try {
                  const photoUrl = await getFileUrl(teacherData.photo, 31536000); // 1 год
                  setProfilePhoto(photoUrl);
                } catch (urlError) {
                  // Используем путь как есть (может быть это уже URL в другом формате)
                  setProfilePhoto(teacherData.photo);
                }
              }
            } else {
              setProfilePhoto(null);
            }
            
            // Обновляем localStorage с правильными данными преподавателя
            localStorage.setItem('currentUser', JSON.stringify({
              id: teacherData.auth_user_id,
              name: teacherData.name,
              email: teacherData.email,
              role: 'teacher',
              phone: teacherData.phone,
              timezone: teacherData.timezone || null
            }));
          } else {
            // Fallback to localStorage if Supabase fails
            const currentUser = getCurrentUserFromStorage();
            if (currentUser && currentUser.role === 'teacher') {
              setProfileData(currentUser);
              setOriginalData(currentUser);
            }
          }
        } else {
          // Fallback to localStorage if Supabase fails
          const currentUser = getCurrentUserFromStorage();
          if (currentUser && currentUser.role === 'teacher') {
            setProfileData(currentUser);
            setOriginalData(currentUser);
          }
        }
      } catch (error) {
        console.error('Error loading user data:', error);
        // Fallback to localStorage if Supabase fails
        const currentUser = getCurrentUserFromStorage();
        if (currentUser && currentUser.role === 'teacher') {
          setProfileData(currentUser);
          setOriginalData(currentUser);
        }
      }
    };
    loadUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount; timezones is static list
  }, []);

  const showSavedToast = () => {
    if (savedToastTimeoutRef.current) clearTimeout(savedToastTimeoutRef.current);
    setSavedToast(true);
    savedToastTimeoutRef.current = setTimeout(() => {
      setSavedToast(false);
      savedToastTimeoutRef.current = null;
    }, 3000);
  };

  const {
    showDeviceSettings,
    openDeviceSettings, closeDeviceSettings, saveDeviceSettings,
    selectedCamera, setSelectedCamera,
    selectedMicrophone, setSelectedMicrophone,
    availableCameras, availableMicrophones,
    isLoadingDevices, videoRef, cameraError, microphoneLevel,
    startCamera, startMicrophone,
  } = useDeviceCheck({ showSavedToast });

  const {
    showPasswordFields,
    passwordData,
    passwordError,
    passwordSaving,
    handleChangePassword, handlePasswordChange, handleSavePassword, handleCancelPassword,
  } = usePasswordChange({ showSavedToast });

  const {
    showEmailFields,
    newEmail,
    currentPasswordForReauth,
    emailError,
    emailSaving,
    emailChangeRequested,
    handleChangeEmail, handleEmailFieldChange, handleSaveEmail, handleCancelEmail,
  } = useEmailChange({ currentEmail: profileData.email });

  const saveTeacherField = async (field, value) => {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) return;
      const { error } = await supabase
        .from('teachers')
        .update({ [field]: value, updated_at: new Date().toISOString() })
        .eq('auth_user_id', authUser.id);
      if (error) console.warn(`ProfileSettings - failed to save teacher ${field}:`, error);
    } catch (e) {
      console.warn(`ProfileSettings - ${field} save exception:`, e);
    }
    showSavedToast();
  };

  const saveTeacherTimezone = async (tzValue) => {
    await saveTeacherField('timezone', tzValue);
    const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
    localStorage.setItem('currentUser', JSON.stringify({ ...currentUser, timezone: tzValue }));
    window.dispatchEvent(new CustomEvent('userDataUpdated'));
  };


  const filteredLanguages = INTERFACE_LANGUAGES.filter(lang =>
    lang.name.toLowerCase().includes(languageSearch.toLowerCase())
  );

  const filteredNativeLanguages = nativeLanguages.filter(lang => 
    lang.name.toLowerCase().includes(nativeLanguageSearch.toLowerCase())
  );

  const filteredCountries = countries.filter(country => 
    country.name.toLowerCase().includes(countrySearch.toLowerCase())
  );

  const filteredTimezones = timezones.filter(timezone => 
    timezone.name.toLowerCase().includes(timezoneSearch.toLowerCase())
  );

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.language-dropdown')) {
        setShowLanguageDropdown(false);
        setShowNativeLanguageDropdown(false);
        setShowCountryDropdown(false);
        setShowTimezoneDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const tabs = [
    { id: 'personal-info', name: 'Personal info' },
    { id: 'notifications', name: 'Notifications' },
    { id: 'security', name: 'Security' },
    { id: 'payments', name: 'Payments and plans' },
  ];

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        // Получаем пользователя
        const { data: { user: authUser } } = await supabase.auth.getUser();
        if (!authUser) {
          alert('Please log in to upload photo');
          return;
        }

        // Загружаем фото в Supabase Storage
        const fileExt = file.name.split('.').pop();
        const fileName = `teachers/${authUser.id}/profile-photo-${Date.now()}.${fileExt}`;
        const photoUrl = await uploadFile(file, fileName, { upsert: true });

        // Обновляем фото в state для предпросмотра
        setProfilePhoto(photoUrl);

        // Сохраняем URL фото в таблицу teachers
        const { data: teacherData } = await supabase
          .from('teachers')
          .select('id')
          .eq('auth_user_id', authUser.id)
          .single();

        if (teacherData) {
          const { error: updateError } = await supabase
            .from('teachers')
            .update({ photo: photoUrl, updated_at: new Date().toISOString() })
            .eq('id', teacherData.id);
          
          if (updateError) {
            // Если ошибка связана с отсутствием поля, показываем понятное сообщение
            if (updateError.message?.includes('column') && updateError.message?.includes('does not exist')) {
              throw new Error('Photo column does not exist in teachers table. Please run SUPABASE_ADD_TEACHER_PHOTO.sql in Supabase SQL Editor.');
            }
            
            throw new Error(`Failed to save photo URL to database: ${updateError.message}`);
          }
          
          // Перезагружаем данные преподавателя, чтобы убедиться, что фото сохранено
          const { data: updatedTeacherData, error: reloadError } = await supabase
            .from('teachers')
            .select('photo')
            .eq('id', teacherData.id)
            .single();
          
          if (!reloadError && updatedTeacherData && updatedTeacherData.photo) {
            setProfilePhoto(updatedTeacherData.photo);
          } else {
            setProfilePhoto(photoUrl);
          }
        }
        showSavedToast();
      } catch (error) {
        console.error('Error uploading photo:', error);
        
        // Более понятные сообщения об ошибках
        let errorMessage = 'Failed to upload photo. ';
        if (error.message?.includes('Bucket not found') || error.message?.includes('does not exist')) {
          errorMessage += 'Storage bucket is not configured. Please contact administrator.';
        } else if (error.message?.includes('Permission denied') || error.message?.includes('RLS')) {
          errorMessage += 'Permission denied. Please check your access rights.';
        } else {
          errorMessage += 'Please try again.';
        }
        
        alert(errorMessage);
      }
    }
  };

  const handleEditPhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handleFieldChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Проверяем есть ли изменения для конкретного поля
    const hasFieldChanges = value !== originalData[field];
    
    if (field === 'name') {
      setHasNameChanges(hasFieldChanges);
    } else if (field === 'phone') {
      setHasPhoneChanges(hasFieldChanges);
    }
  };

  const handleSave = async () => {
    try {
      // Получаем пользователя напрямую из Supabase Auth
      const { data: { user: authUser } } = await supabase.auth.getUser();
      
      if (!authUser) {
        throw new Error('User not authenticated');
      }

      // Получаем данные преподавателя из таблицы teachers
      const { data: teacherData, error: fetchError } = await supabase
        .from('teachers')
        .select('*')
        .eq('auth_user_id', authUser.id)
        .single();

      if (fetchError || !teacherData) {
        throw new Error('Teacher not found in database');
      }

      // Update in Supabase - обновляем данные преподавателя
      const updateData = {
        name: profileData.name,
        phone: profileData.phone,
        timezone: selectedTimezone?.value || null,
        updated_at: new Date().toISOString()
      };

      // Если фото было загружено (это URL из Storage), сохраняем его
      if (profilePhoto && profilePhoto.startsWith('http')) {
        updateData.photo = profilePhoto;
      }

      const { error } = await supabase
        .from('teachers')
        .update(updateData)
        .eq('auth_user_id', authUser.id);

      if (error) {
        throw error;
      }

      // Update localStorage for compatibility
      const updatedUser = {
        id: authUser.id,
        name: profileData.name,
        email: teacherData.email, // Используем email из базы, а не из profileData
        role: 'teacher',
        phone: profileData.phone,
        timezone: selectedTimezone?.value || null
      };
      localStorage.setItem('currentUser', JSON.stringify(updatedUser));
      
      // Dispatch custom event to notify other components
      window.dispatchEvent(new CustomEvent('userDataUpdated'));
      
      setOriginalData({...profileData});
      setHasNameChanges(false);
      setHasPhoneChanges(false);
      showSavedToast();
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Failed to save profile. Please try again.');
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'personal-info':
        return (
          <div className="profile-content">
            <div className="profile-header">
              <h1>Personal profile</h1>
              <p className="profile-header-subtitle">Manage your personal details and preferences.</p>
            </div>

            <div className="profile-right">
                <div className="settings-section">
                <h2 className="settings-section-title">Basic info</h2>
                <div className="settings-row-avatar">
                  <div className="profile-photo-circle" onClick={handleEditPhotoClick}>
                    {profilePhoto ? (
                      <img
                        src={profilePhoto}
                        alt="Profile"
                        className="profile-photo-img"
                        onError={(e) => {
                          // Если фото не загружается, скрываем его и показываем инициалы
                          e.target.style.display = 'none';
                          setProfilePhoto(null);
                        }}
                      />
                    ) : (
                      <span className="profile-initial">N</span>
                    )}
                  </div>
                  <div className="settings-row-avatar-field">
                    <label>Name</label>
                    <div className="input-with-save">
                      <input
                        type="text"
                        value={profileData.name}
                        onChange={(e) => handleFieldChange('name', e.target.value)}
                        className="profile-input"
                      />
                      {hasNameChanges && (
                        <button className="profile-save-btn" onClick={handleSave}>
                          Save
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                <button className="edit-photo-btn" onClick={handleEditPhotoClick}>Change photo</button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handlePhotoUpload}
                  accept="image/*"
                  style={{ display: 'none' }}
                />

                <div className="profile-field">
                  <label>Email</label>
                  {!showEmailFields ? (
                    <div className="password-field">
                      <input
                        type="email"
                        value={profileData.email}
                        className="profile-input profile-input-disabled"
                        disabled
                        readOnly
                      />
                      <button className="change-btn" onClick={handleChangeEmail}>Change</button>
                    </div>
                  ) : (
                    <div className="password-change-fields">
                      <input
                        type="email"
                        placeholder="New email"
                        value={newEmail}
                        onChange={(e) => handleEmailFieldChange('newEmail', e.target.value)}
                        className="password-change-input"
                      />
                      <input
                        type="password"
                        placeholder="Current password"
                        value={currentPasswordForReauth}
                        onChange={(e) => handleEmailFieldChange('currentPasswordForReauth', e.target.value)}
                        className="password-change-input"
                      />
                      {emailError && (
                        <div style={{
                          marginTop: 8,
                          padding: '8px 12px',
                          background: '#fef2f2',
                          border: '1px solid #fecaca',
                          borderRadius: 8,
                          color: '#b91c1c',
                          fontSize: 13,
                          lineHeight: 1.4,
                        }}>
                          {emailError}
                        </div>
                      )}
                      <div className="password-change-buttons">
                        <button className="password-cancel-btn" onClick={handleCancelEmail} disabled={emailSaving}>
                          Cancel
                        </button>
                        <button className="password-save-btn" onClick={handleSaveEmail} disabled={emailSaving}>
                          {emailSaving ? 'Saving…' : 'Save'}
                        </button>
                      </div>
                    </div>
                  )}
                  {emailChangeRequested && (
                    <p style={{ marginTop: 8, fontSize: 13, color: '#495057' }}>
                      Confirmation email sent to your new address — click the link there to finish the change.
                    </p>
                  )}
                </div>

                <div className="profile-field">
                  <label>Phone number</label>
                  <div className="input-with-save">
                    <input
                      type="tel"
                      placeholder="Your phone number"
                      value={profileData.phone}
                      onChange={(e) => handleFieldChange('phone', e.target.value)}
                      className="profile-input"
                    />
                    {hasPhoneChanges && (
                      <button className="profile-save-btn" onClick={handleSave}>
                        Save
                      </button>
                    )}
                  </div>
                </div>
                </div>

                <div className="settings-section">
                <h2 className="settings-section-title">Region &amp; language</h2>
                <SearchableDropdown
                  label="Interface language"
                  isOpen={showLanguageDropdown}
                  onToggle={() => setShowLanguageDropdown(!showLanguageDropdown)}
                  onClose={() => setShowLanguageDropdown(false)}
                  searchTerm={languageSearch}
                  onSearchChange={setLanguageSearch}
                  searchPlaceholder="Search language..."
                  options={filteredLanguages}
                  selected={selectedLanguage}
                  onSelect={(lang) => { setSelectedLanguage(lang); saveTeacherField('interface_language', lang.value); }}
                />
                <p style={{ margin: '4px 0 20px', fontSize: 12, color: '#6c757d' }}>
                  More languages coming soon — the interface is English-only for now.
                </p>

                <SearchableDropdown
                  label="Native language"
                  isOpen={showNativeLanguageDropdown}
                  onToggle={() => setShowNativeLanguageDropdown(!showNativeLanguageDropdown)}
                  onClose={() => setShowNativeLanguageDropdown(false)}
                  searchTerm={nativeLanguageSearch}
                  onSearchChange={setNativeLanguageSearch}
                  searchPlaceholder="Search language..."
                  options={filteredNativeLanguages}
                  selected={selectedNativeLanguage}
                  onSelect={(lang) => { setSelectedNativeLanguage(lang); saveTeacherField('native_language', lang.value); }}
                />

                <SearchableDropdown
                  label="Country"
                  isOpen={showCountryDropdown}
                  onToggle={() => setShowCountryDropdown(!showCountryDropdown)}
                  onClose={() => setShowCountryDropdown(false)}
                  searchTerm={countrySearch}
                  onSearchChange={setCountrySearch}
                  searchPlaceholder="Search country..."
                  options={filteredCountries}
                  selected={selectedCountry}
                  onSelect={(country) => { setSelectedCountry(country); saveTeacherField('country', country.value); }}
                />

                <SearchableDropdown
                  label="Timezone"
                  isOpen={showTimezoneDropdown}
                  onToggle={() => setShowTimezoneDropdown(!showTimezoneDropdown)}
                  onClose={() => setShowTimezoneDropdown(false)}
                  searchTerm={timezoneSearch}
                  onSearchChange={setTimezoneSearch}
                  searchPlaceholder="Search timezone..."
                  options={filteredTimezones}
                  selected={selectedTimezone}
                  onSelect={(tz) => { setSelectedTimezone(tz); saveTeacherTimezone(tz.value); }}
                  showFlag={false}
                  contentClassName="timezone-dropdown-content"
                />
                </div>

                <div className="settings-section">
                <h2 className="settings-section-title">Video conferencing</h2>
                <div className="profile-field">
                  <label>Check your camera and microphone</label>
                  <button
                    className="video-check-btn"
                    onClick={openDeviceSettings}
                  >
                    Check
                  </button>
                </div>
                </div>
            </div>
          </div>
        );
      case 'notifications':
        return <NotificationsTab showSavedToast={showSavedToast} />;
      case 'security':
        return (
          <SecurityTab
            showPasswordFields={showPasswordFields}
            passwordData={passwordData}
            passwordError={passwordError}
            passwordSaving={passwordSaving}
            handleChangePassword={handleChangePassword}
            handlePasswordChange={handlePasswordChange}
            handleSavePassword={handleSavePassword}
            handleCancelPassword={handleCancelPassword}
            showSavedToast={showSavedToast}
          />
        );
      case 'payments':
        return <PaymentsPlanTab />;
      default:
        return null;
    }
  };

  return (
    <div className="profile-settings-page">
      <div className="profile-settings-container">
        {/* Left Navigation */}
        <div className="profile-nav">
          <div className="profile-nav-header">
            <h1>Profile Settings</h1>
            {/* Mobile only — desktop's sign-out sits below the tab list
                (.profile-signout-btn-bottom) instead. */}
            <button
              type="button"
              className="profile-signout-btn profile-signout-btn-header"
              onClick={() => setShowSignOutConfirm(true)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              <span>Sign out</span>
            </button>
          </div>

          <div className="profile-nav-tabs">
            {tabs.map(tab => (
              <div
                key={tab.id}
                className={`nav-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <NavIcon id={tab.id} />
                <span>{tab.name}</span>
              </div>
            ))}
          </div>

          {/* Desktop only — mobile uses .profile-signout-btn-header instead. */}
          <button
            type="button"
            className="profile-signout-btn profile-signout-btn-bottom"
            onClick={() => setShowSignOutConfirm(true)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            <span>Sign out</span>
          </button>
        </div>

        {/* Right Content */}
        <div className="profile-main">
          {renderContent()}
        </div>
      </div>

      <DeviceSettingsModal
        showDeviceSettings={showDeviceSettings}
        closeDeviceSettings={closeDeviceSettings}
        saveDeviceSettings={saveDeviceSettings}
        isLoadingDevices={isLoadingDevices}
        selectedCamera={selectedCamera}
        setSelectedCamera={setSelectedCamera}
        availableCameras={availableCameras}
        startCamera={startCamera}
        cameraError={cameraError}
        videoRef={videoRef}
        selectedMicrophone={selectedMicrophone}
        setSelectedMicrophone={setSelectedMicrophone}
        availableMicrophones={availableMicrophones}
        startMicrophone={startMicrophone}
        microphoneLevel={microphoneLevel}
      />

      {savedToast && (
        <div className="profile-settings-saved-toast" role="status" aria-live="polite">
          <div className="profile-settings-saved-toast-inner">
            <span className="profile-settings-saved-toast-icon" aria-hidden>✓</span>
            <p className="profile-settings-saved-toast-message">Changes are saved</p>
            <button type="button" className="profile-settings-saved-toast-close" onClick={() => setSavedToast(false)} aria-label="Close">×</button>
          </div>
        </div>
      )}

      <ConfirmSignOutModal
        isOpen={showSignOutConfirm}
        onCancel={() => setShowSignOutConfirm(false)}
        onConfirm={handleSignOut}
      />
    </div>
  );
};

export default ProfileSettings;

